<template>
    <div class="gallery-item" :data-id="item.id" :class="selectItemClasses" @click="onClick">

        <div class="gallery-link">
            <div class="gallery-thumb-container">
                <thumbnail :item="item" />
                <media-icon v-if="!condensed" :item="item" />
            </div>

             <div class="d-flex flex-column w-100">
                <div class="gallery-item-title-wrap">
                    <h5 class="gallery-item-title single-line flex-grow">{{ item.title }}</h5>
                    <div
                        v-if="item.versionCount > 1"
                        class="badge version-badge"
                        title="File Revision"
                        v-tippy="{arrow: true}">
                        {{ item.file.badge | capitalize }}
                    </div>
                </div>
                <slot v-bind:item="item"></slot>

                <div class="gallery-item-meta single-line">
                    <div class="gallery-item-meta-detail">
                        <i class="fa fa-clock"></i>
                        <span title="Last updated" v-tippy="{arrow: true}">{{ item.lastUpdated }}</span>
                        {{ item.uploadedBy }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { capitalize } from 'lodash';
import ProgressBar from 'vue-simple-progress';

import Thumbnail from '@/components/media/Thumbnail';
import MediaIcon from '@/components/media/MediaIcon';

export default {
    name: 'SelectItem',
    components: {
        MediaIcon,
        Thumbnail,
        ProgressBar
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        mode: {
            type: String,
            default: 'multiple',
            validator: v => ['multiple', 'single'].indexOf(v) !== -1
        },
        isChecked: {
            type: Boolean,
            default: false
        },
        condensed: {
            type: Boolean,
            default: false,
        },
        selectable: {
            type: Boolean,
            default: true,
        },
        selectedGetter: {
            type: String,
            default: 'getMediaSelectSelected',
        },
        selectedSetter: {
            type: String,
            default: 'addMediaSelectItem',
        },
    },
    filters: {
        capitalize,
    },
    data() {
        return {
            progressVal: 0,
        }
    },
    computed: {
        selected() {
            if (this.selectedGetter) {
                return this.$store.getters[this.selectedGetter];
            }
        },
        showVersionBadge() {
            return this.item.hasFiles && this.item.file.badge !== 'v1';
        },
        selectItemClasses() {
            return {
                ...this.item.itemClasses,
                'is-selected': this.isChecked,
                'is-condensed': this.condensed,
            };
        },
    },
    watch: {
        isProcessing: {
            immediate: true,
            handler: function (value) {
                if (value) {
                    this.setProccessing();
                } else {
                    this.clearProcessing();
                }
            }
        },
    },
    methods: {
        setSelected(item) {
            return this.$store.dispatch(this.selectedSetter, item);
        },
        onClick(e) {
            e.preventDefault();
            e.stopPropagation();
            if (this.item.isOk && this.selectable) {
                this.setSelected({
                    id: this.item.id,
                    checked: !this.isChecked,
                    mode: this.mode,
                    type: this.item.isMediaSet ? 'set' : 'media',
                });

                this.$emit('item-clicked', {e, item: this.item});
            }
        },
        setProccessing() {
            this.interval = setInterval(() => {
                if (this.progressVal === 100) this.progressVal = 0; // reset if not complete
                this.progressVal += 10;
            }, 1000);
        },
        clearProcessing() {
            if (this.interval) clearInterval(this.interval);
        },
    }
}
</script>

<style lang="scss" scoped>
@import '~/sass/_variables';

.gallery-item {
    position: relative;
    cursor: pointer;
    width: 100%;
    background: white;
    opacity: 0.7;

    &.is-pending, &.is-processing {

        &:before {
            content: '';
            display: flex;
            align-items: flex-start;
            justify-content: center;
            position: absolute;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            z-index: 4;
            background: rgba(white, 0.6);
            font-weight: 600;
            padding-top: 21%; // try and align text to center of image
            color: #28a5d4;
            font-size: 2rem;
            font-style: italic;
            cursor: not-allowed;
        }
    }

    &.is-pending:after, &.is-processing:after {
        content: 'Upload processing...';
        position: absolute;
        background: #FFF;
        padding: 0.6em 1.2em;
        border-radius: 3px;
        z-index: 5;
        top: 25%;
        left: 50%;
        text-align: center;
        font-style: italic;
        transform: translate(-50%,0);
        box-shadow: 0 0 1em rgba(0,0,0,0.1);
        font-size: 1.4rem;
        white-space: nowrap;
    }

    &:active, &:hover {
        opacity: 1;
    }

    &.is-selected:not(.is-pending):not(.is-processing),
    &:active:not(.is-pending):not(.is-processing),
    &:hover:not(.is-pending):not(.is-processing) {
        position: relative;
        opacity: 1;

        &:before {
            position: absolute;
            content: '\f058';
            font-family: "Font Awesome 5 Pro";
            font-size: 24px;
            box-shadow: 0 0 0 2px white;
            border-radius: 10px 10px;
            top: 1rem;
            left: 1rem;
            z-index: 999;
            line-height: 21px;
            background: white;
        }
    }

    &.is-selected:not(.is-pending):not(.is-processing),
    &:active:not(.is-pending):not(.is-processing) {
        box-shadow: 0 0 0 2px $brand-success;

        &:before {
            color: $brand-success;
        }
    }

    &:hover:not(.is-pending):not(.is-processing) {
        box-shadow: 0 0 0 2px $brand-primary;

         &:before {
             color: $brand-primary;
        }
    }

    .gallery-thumb-container {
        pointer-events: none;
    }
}
</style>
